import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {emptyFn} from "shared";
import {debounce as debounceFunction} from 'throttle-debounce';

import styles from './Input.module.scss';

export const Input = (props) => {
    const {
        label = false,
        type = 'text',
        placeholder,
        help,
        className,
        error,
        required,
        prefixIcon = false,
        prefixTop = 0,
        prefixLeft = 8,
        suffixRight = 8,
        suffixTop = 0,
        classNameInputPrefixIcon,
        inputLeft = 30,
        onChange,
        onTextChange,
        onBlur,
        value = '',
        debounce,
        suffixIcon,
        ...other
    } = props;

    const [innerValue, setInnerValue] = useState(value);
    const emitChange = useCallback(
        (event) => {
            onChange?.(event);
            onTextChange?.(event.target.value);
        },
        [onChange, onTextChange],
    );

    const handleDebounce = useCallback(
        debounceFunction(debounce, (event) => {
            emitChange(event);
        }),
        [],
    );

    const handleChange = useCallback(
        (event) => {
            setInnerValue(event.target.value);

            if (debounce) {
                handleDebounce(event);
                return;
            }

            emitChange(event);
        },
        [debounce, onChange, onTextChange],
    );

    useEffect(() => {
        setInnerValue(value);
    }, [value]);

    return (
        <div
            className={clsx('form-group d-flex flex-column', styles.input, {[styles.inputPrefix]: prefixIcon, [styles.inputSuffix]: suffixIcon}, className)}>
            {label && (<label> {required && <span className="required">*</span>} {label}</label>)}
            {suffixIcon && (
                <span className={styles.prefixIcon} style={{top: `${suffixTop}px`, right: `${suffixRight}px`}}>
                  {suffixIcon}
                </span>
            )}
            <input
                type={type}
                value={innerValue}
                onChange={handleChange}
                onBlur={onBlur}
                className={clsx('form-control', styles.inputInner, {"is-invalid": error}, classNameInputPrefixIcon)}
                style={{paddingLeft: prefixIcon ? `${inputLeft}px` : "10px"}}
                placeholder={placeholder}
                {...other}/>
            {prefixIcon && (
                <span className={styles.prefixIcon} style={{top: `${prefixTop}px`, left: `${prefixLeft}px`}}>
                  {prefixIcon}
                </span>
            )}
            {help && !error && <small className="form-text text-muted">{help}</small>}
            {error && <small className={clsx('form-text', styles.invalidFeedback)}>{error}</small>}
        </div>
    );
};

Input.defaultProps = {
    type: 'text',
    help: '',
    placeholder: '',
    className: '',
    onChange: emptyFn,
    onBlur: emptyFn,
    value: '',
};

Input.propTypes = {
    label: PropTypes.string,
    type: PropTypes.string,
    placeholder: PropTypes.string,
    help: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    prefixIcon: PropTypes.node,
    suffixIcon: PropTypes.node,
    className: PropTypes.string,
    classNameInputPrefixIcon: PropTypes.string,
    inputLeft: PropTypes.number,
    prefixTop: PropTypes.number,
   suffixTop: PropTypes.number,
    prefixLeft: PropTypes.number,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.string,
    debounce: PropTypes.number,
    onTextChange: PropTypes.func
};

export default Input;
